import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material';
import { GetApp } from '@mui/icons-material';
import { formatShortMonthDayYear } from '../../../shared/Dates';
import { client, consoleLogInDev, formatCurrency } from 'shared';
import download from 'shared/Download';
import { Colors } from 'library';
import {
    Card,
    Chip,
    IconButton,
    Modal,
    ModalFooter,
    PillButton,
    Row,
    Select,
    Stack,
    Table,
    TableColumn,
    Text,
    Title,
} from 'library/ui';
import { useUserContext } from 'UserContext';

interface ILineItem {
    amount: number;
}
interface IInvoice {
    id: number;
    amount: number;
    amount_paid?: number;
    paid_date: string;
    line_items: ILineItem[];
    bill_date: string;
}

const COMPONENT_STATES = {
    neutral: 'neutral',
    submittingPayment: 'submittingPayment',
    confirmSubmitPayment: 'confirmSubmitPayment',
    downloading: 'downloading',
};

interface FailedPaymentsProps {
    refreshKey: number;
    onPaymentsRetried: () => void;
}

const FailedPayments = ({ refreshKey, onPaymentsRetried }: FailedPaymentsProps) => {
    const [componentState, setComponentState] = useState(COMPONENT_STATES.neutral);
    const [failedPayments, setFailedPayments] = useState<IInvoice[]>([]);
    const [selectedFailedPayment, setSelectedFailedPayment] = useState<number>();
    const [selectedPaymentInfo, setSelectedPaymentInfo] = useState<number>();
    const [errorMessage, setErrorMessage] = useState('');
    const { user, role } = useUserContext();

    const isBusiness = role === 'business_active';

    useEffect(() => {
        getFailedPayments();
    }, [refreshKey]);

    const getFailedPayments = () => {
        client('payment/api/invoice/?status=overdue&size=100')
            .then((response) => {
                setFailedPayments(response.results);
            })
            .catch(consoleLogInDev);
    };

    const onSelectFailedPayment = (id: number) => {
        setSelectedFailedPayment(id);
        setComponentState(COMPONENT_STATES.confirmSubmitPayment);
    };

    const onRetryPayment = () => {
        setComponentState(COMPONENT_STATES.submittingPayment);
        setErrorMessage('');

        client(`payment/api/invoice/${selectedFailedPayment}/retry/`, {
            body: { payment_information_id: selectedPaymentInfo },
        })
            .then(() => {
                setSelectedFailedPayment(undefined);
                setSelectedPaymentInfo(undefined);
                onPaymentsRetried();
            })
            .catch((error) => {
                setErrorMessage(error.message || 'There was an error retrying your payment');
            })
            .finally(() => setComponentState(COMPONENT_STATES.neutral));
    };

    const onDialogClose = () => {
        setComponentState(COMPONENT_STATES.neutral);
        setSelectedPaymentInfo(undefined);
    };

    const onDownloadClick = (invoiceId: number) => {
        setComponentState(COMPONENT_STATES.downloading);
        setErrorMessage('');
        download(`payment/api/invoice/${invoiceId}/download/`)
            .catch((error) => {
                consoleLogInDev(error);
                setErrorMessage('There was an error generating your invoice');
            })
            .finally(() => {
                setComponentState(COMPONENT_STATES.neutral);
            });
    };

    if (!failedPayments?.length) {
        return <></>;
    }

    const selectOptions = [
        { key: 0, value: 'Select One' },
        ...(user?.payment_information || []).map((info) => {
            const infoData = info.data;
            return {
                key: info.id,
                value: `Account ending in ${infoData?.last4 || info.last_four}`,
            };
        }),
    ];

    const failedTotal = failedPayments.reduce((sum, item) => sum + item.amount - (item.amount_paid || 0), 0);

    const columns: TableColumn<IInvoice>[] = [
        { key: 'id', label: 'INVOICE', render: (row) => <Text weight="bold">{row.id}</Text> },
        {
            key: 'date',
            label: 'PAYMENT DATE',
            render: (row) => formatShortMonthDayYear(row.bill_date || row.paid_date),
        },
        {
            key: 'amount',
            label: 'AMOUNT OWED',
            render: (row) => <Text weight="bold">{`$${(row.amount - (row.amount_paid || 0)).toFixed(2)}`}</Text>,
        },
        {
            key: 'download',
            label: '',
            render: (row) =>
                isBusiness ? (
                    <IconButton
                        icon={GetApp}
                        tooltip="Download"
                        disabled={componentState === COMPONENT_STATES.downloading}
                        onClick={() => onDownloadClick(row.id)}
                    />
                ) : null,
        },
        {
            key: 'retry',
            label: '',
            render: (row) => (
                <PillButton
                    variant="secondary"
                    color={Colors.warning}
                    disabled={componentState === COMPONENT_STATES.submittingPayment}
                    onClick={() => onSelectFailedPayment(row.id)}
                >
                    {componentState === COMPONENT_STATES.submittingPayment ? 'Retrying...' : 'Retry'}
                </PillButton>
            ),
        },
    ];

    return (
        <>
            <StyledCard padding="lg">
                <Stack gap="sm">
                    <Row justify="between" align="center">
                        <Title size="h2">Failed Payments</Title>
                        <Chip variant="filled" bgColor={Colors.warningBackground} textColor={Colors.warning}>
                            {formatCurrency(failedTotal)} owed
                        </Chip>
                    </Row>
                    <Text component="p">
                        There was an issue charging your default method. Update it or confirm funds are available, then
                        retry the payments below.
                    </Text>
                    <TableScroll>
                        <Table columns={columns} data={failedPayments} />
                    </TableScroll>
                    {errorMessage && <Text tone="error">{errorMessage}</Text>}
                </Stack>
            </StyledCard>
            {componentState === COMPONENT_STATES.confirmSubmitPayment && (
                <Modal title="Pay Invoice" onClose={onDialogClose}>
                    <div>
                        <Text component="p">Select payment method</Text>
                        <Select
                            value={selectedPaymentInfo || 0}
                            options={selectOptions}
                            onChange={(value) => setSelectedPaymentInfo(Number(value))}
                        />
                    </div>
                    <ModalFooter>
                        <PillButton variant="secondary" onClick={onDialogClose}>
                            Cancel
                        </PillButton>
                        <PillButton
                            disabled={!selectedPaymentInfo || componentState === COMPONENT_STATES.submittingPayment}
                            onClick={onRetryPayment}
                        >
                            {componentState === COMPONENT_STATES.submittingPayment ? 'Paying...' : 'OK'}
                        </PillButton>
                    </ModalFooter>
                </Modal>
            )}
        </>
    );
};

const TableScroll = styled('div')({
    maxWidth: '100%',
    overflowX: 'auto',
});

const StyledCard = styled(Card)({
    border: `1px solid ${Colors.warning}`,
});

export default FailedPayments;
